import { FaroCustomCheckbox } from "@components/common/faro-table/faro-custom-checkbox";
import { ListItemMenu } from "@components/common/mobile/list-item-menu";
import {
  Identifiable,
  MenuItemOption,
} from "@components/common/mobile/mobile-types";
import { SphereTooltip } from "@components/common/sphere-tooltip";
import { neutral, Text } from "@flat-ui";
import { Box, Stack } from "@mui/material";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { selectedRowIdsSelector } from "@store/table/table-selector";
import { setInitialFetchingItems } from "@store/table/table-slice";
import { withEllipsis } from "@styles/common-styles";
import { toggleItemSelection } from "@utils/array-utils";
import { MouseEvent, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { SphereActionDivider } from "@components/common/sphere-action-divider";

interface Props<T extends Identifiable> {
  /** The item being rendered */
  item: T;

  /** Thumbnail or image URL shown on the left */
  image: ReactNode;

  /** Alt text for the image, used if image is a string URL */
  imageAlt?: string;

  /** Main title text */
  title: ReactNode;

  /** Optional URL; when provided, tile renders as a link instead of a clickable div */
  href?: string;

  /** Optional callback triggered when the card is clicked. Can coexist with href. */
  onCardClick?: () => void;

  /** Subtitle text shown above the title */
  subtitle: ReactNode;

  /** Optional tooltip to show on subtitle hover/long-press */
  subtitleTooltip?: JSX.Element;

  /** Secondary text shown at the bottom */
  secondaryText?: string;

  /** Optional actions component to render on the right side of the card */
  actions?: JSX.Element;

  /** Optional menu options; when provided a three-dots menu is shown. */
  menuItems?: MenuItemOption<T>[];
}

/**
 * Renders a mobile-friendly list item card with image, title, subtitle, optional secondary
 * text and an optional three-dots menu.
 *
 * @template T - The type of item being rendered
 */
export function ListItemCardMobile<T extends Identifiable>({
  item,
  image,
  imageAlt,
  title,
  href,
  onCardClick,
  subtitle,
  subtitleTooltip,
  secondaryText,
  actions,
  menuItems,
}: Props<T>): JSX.Element {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const selectedRowIds = useAppSelector(selectedRowIdsSelector);

  // Get the item ID, using identity as fallback if id doesn't exist
  const itemId = item.id ?? item.identity ?? "";

  if (!itemId) {
    throw new Error("Item must have either 'id' or 'identity' property");
  }

  const isItemSelected = selectedRowIds.includes(itemId);
  const shouldShowCheckbox = selectedRowIds.length > 0;
  const hasMenuItems = (menuItems?.length ?? 0) > 0;
  const shouldShowActions =
    !shouldShowCheckbox && (hasMenuItems || actions !== undefined);
  const shouldShowActionsDivider = actions !== undefined && hasMenuItems;
  const isClickable = (!!href || !!onCardClick) && !shouldShowCheckbox;
  const component = href ? "a" : "div";

  function handleCheckboxChange(): void {
    const newSelectedRowIds = toggleItemSelection(selectedRowIds, itemId);
    dispatch(setInitialFetchingItems(newSelectedRowIds));
  }

  function handleLinkClick(e: MouseEvent<HTMLAnchorElement>): void {
    // Allow modifier keys (ctrl, cmd, shift) and middle click to open in new tab
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) {
      return;
    }

    e.preventDefault();
    if (href) {
      navigate(href);
    }
  }

  /** Returns the props to be spread onto the root component based on the href and onCardClick */
  function getComponentProps(): object {
    if (!isClickable) {
      return {};
    }

    if (href) {
      return { href, onClick: handleLinkClick };
    }

    if (onCardClick) {
      return { onClick: onCardClick };
    }

    return {};
  }

  return (
    <Stack
      component={component}
      direction="row"
      gap={1}
      p={1}
      {...getComponentProps()}
      sx={{
        cursor: isClickable ? "pointer" : undefined,
        textDecoration: "none",
        color: "inherit",
        height: "100%",
      }}
    >
      {typeof image === "string" ? (
        <Box
          component="img"
          src={image}
          alt={imageAlt}
          sx={{ width: 132, height: 96, objectFit: "cover" }}
        />
      ) : (
        image
      )}

      <Stack flex={1} minWidth={0} justifyContent="space-between">
        <Stack gap={0.5}>
          <SphereTooltip
            title={subtitleTooltip ?? ""}
            shouldShowTooltip={!!subtitleTooltip}
            shouldSkipWrapper
          >
            <Text variant="bodyS" sx={{ ...withEllipsis }}>
              {subtitle}
            </Text>
          </SphereTooltip>
          <Text component="var" variant="heading16" sx={withEllipsis}>
            {title}
          </Text>
        </Stack>
        <Text
          variant="bodyS"
          sx={{
            textTransform: "capitalize",
            color: neutral[700],
            ...withEllipsis,
          }}
        >
          {secondaryText}
        </Text>
      </Stack>

      {shouldShowCheckbox && (
        <Stack
          alignItems="center"
          justifyContent="center"
          onClick={(e) => e.stopPropagation()}
        >
          <FaroCustomCheckbox
            checked={isItemSelected}
            onChange={handleCheckboxChange}
            sx={{
              // Always show this checkbox in this specific case.
              // The parent component is usually hidden and only appears on hover,
              // but here we want the checkbox to remain visible at all times.
              visibility: "visible",
            }}
          />
        </Stack>
      )}

      {shouldShowActions && (
        <Stack direction="row" alignItems="center">
          {actions}
          {shouldShowActionsDivider && <SphereActionDivider />}
          {menuItems && menuItems.length > 0 && (
            <ListItemMenu<T> item={item} menuItems={menuItems} />
          )}
        </Stack>
      )}
    </Stack>
  );
}
